import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cloneDeep } from 'lodash';
import {
    Card,
    CardHeader,
    CardContent,
    Avatar,
    Typography,
    Select,
    MenuItem,
    Button,
    TextField,
    Grid,
    IconButton,
} from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import AutoComplete from '@material-ui/lab/Autocomplete';
import { ArrowUpward, ArrowDownward, Delete as DeleteIcon } from '@material-ui/icons';
import { UPDATE_AWARDING_RULE } from '../../../store/awardingRule';
import { SortOrderType, DataviewFieldDataType } from '../../../../Enums';
import { getSortOrderTypeLabel } from '../../../../Enums/Helpers/LabellingHelper';
import '../../../SetupList/SetupEdit/SetupEdit.scss';
import './ParametersSection.scss';

const InputTitle = (props) => (
    <Typography
        component="p"
        className="input-label"
        {...props}
    >
        {props.children}
    </Typography>
);

const StyledSelect = (props) => (
    <Select
        MenuProps={{
            anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
            },
            transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
            },
            getContentAnchorEl: null,
        }}
        variant="outlined"
        className="sue-select-full-width"
        defaultValue=""
        {...props}
    >
        {props.children}
    </Select>
);

/**
 * @type {AwardingRule.IDataviewAwardingParameter}
 */
const DEFAULT_PARAMETER = {
    dataviewKeyAlias: '',
    sortOrderType: SortOrderType.Ascending,
    dataType: DataviewFieldDataType.Numeric,
    customDateFormatId: undefined,
};

/**
 * @param {{
 *  validationErrors: {[Key: string] : boolean},
 *  isDisabled: boolean
 * }} props
 */
function ParametersSection(props) {
    const { validationErrors, isDisabled } = props;
    const dispatch = useDispatch();
    /** @type {ShiftBidding.AdminStore} */
    // @ts-ignore
    const { adminAwardingRule, adminAppData } = useSelector((state) => state);
    const {
        dataviewId, parameters,
    } = adminAwardingRule;
    const { dataviews } = adminAppData;

    const [fieldValidationErrors, setFieldValidationErrors] = useState([false]);// true if invalid

    /**
     * @param {string} target
     * @param { number | boolean | AwardingRule.IDataviewAwardingParameter[] | AwardingRule.IDataviewAwardingParameter
     * } value
     */
    const updateAwardingRuleStore = (target, value) => {
        dispatch({
            type: UPDATE_AWARDING_RULE,
            payload: {
                [target]: value,
            },
        });
    };

    const resetDataviewFields = () => {
        updateAwardingRuleStore('parameters', [cloneDeep(DEFAULT_PARAMETER)]);
    };

    /**
     * @param {number} id
     */
    const handleDataviewOptionSelected = (id) => {
        resetDataviewFields();
        updateAwardingRuleStore('dataviewId', id);
    };

    /**
     * @param {number} index
     * @param {string} target
     * @param {string | number} value
     */
    const handleDataviewFieldOptionSelected = (index, target, value) => {
        const updatedParameter = cloneDeep(parameters[index]);
        if (target === 'dataType') {
            if (value === DataviewFieldDataType.Numeric) {
                updatedParameter[target] = 0;
                updatedParameter.customDateFormatId = undefined;
            } else {
                updatedParameter[target] = 1;
                // @ts-ignore
                updatedParameter.customDateFormatId = value;
            }
        } else {
            // @ts-ignore
            updatedParameter[target] = value;
        }
        parameters[index] = updatedParameter;

        const updatedValidations = parameters.map((x) => !x.dataviewKeyAlias);
        setFieldValidationErrors(updatedValidations);
        dispatch({
            type: UPDATE_AWARDING_RULE,
            payload: {
                parameters,
            },
        });
    };

    const handleAddParameter = () => {
        const invalidFieldIndex = parameters.findIndex((x) => !x.dataviewKeyAlias);
        if (invalidFieldIndex !== -1) {
            const updatedValidationErrors = parameters.map((x, index) => index === invalidFieldIndex);
            setFieldValidationErrors(updatedValidationErrors);
            return;
        }
        const updatedParameters = [...parameters, cloneDeep(DEFAULT_PARAMETER)];
        updateAwardingRuleStore('parameters', updatedParameters);
    };

    return (
        <Card className="card">
            <CardHeader
                className="card--header"
                color="textSecondary"
                avatar={<Avatar className="avatar">{2}</Avatar>}
                title="Parameters"
            />
            <CardContent>
                <div>
                    <InputTitle>Dataview</InputTitle>
                    <AutoComplete
                        id="dataview-select"
                        value={dataviews.find((x) => x.id === dataviewId)}
                        options={dataviews}
                        onChange={(_event, optionSelected) => handleDataviewOptionSelected(optionSelected?.id)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                placeholder="Select a dataview"
                                style={{ paddingBottom: '20px' }}
                            />
                        )}
                        getOptionLabel={(option) => option.name}
                        includeInputInList
                        disabled={isDisabled}
                    />
                    {
                        validationErrors.dataviewId ? <span style={{ color: 'red' }}>Please select a dataview</span> : null
                    }
                </div>
                {dataviewId !== -1 && (
                    <div className="dataview-fields--order">
                        {
                            parameters.map((dataviewField, index) => (
                                <RuleSetParameter
                                    key={`DataviewField_${dataviewField.dataviewKeyAlias}_${dataviewField.sortOrderType}}`}
                                    dataviewParam={dataviewField}
                                    index={index}
                                    isFieldInvalid={fieldValidationErrors[index]}
                                    handleOptionSelected={handleDataviewFieldOptionSelected}
                                    isDisabled={isDisabled}
                                />
                            ))
                        }

                        {
                            validationErrors.parameters ? (
                                <span style={{ color: 'red', display: 'block' }}>Please select a dataview field</span>) : null
                        }
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={handleAddParameter}
                            disabled={isDisabled}
                        >
                            <FontAwesomeIcon
                                className="awarding-param-icon-plus"
                                icon={faPlus}
                            />
                            Add Parameter
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

// eslint-disable-next-line import/prefer-default-export
export const Direction = Object.freeze({
    Up: -1,
    Down: 1,
});

/**
 * @param {{
 *  dataviewParam: AwardingRule.IDataviewAwardingParameter,
 *  index: number,
 *  isFieldInvalid: boolean,
 *  handleOptionSelected: Function,
 *  isDisabled: boolean
 * }} props
 */
function RuleSetParameter(props) {
    const {
        dataviewParam,
        index = 0,
        isFieldInvalid,
        handleOptionSelected = () => { },
        isDisabled,
    } = props;
    const dispatch = useDispatch();

    /** @type {ShiftBidding.AdminStore} */
    // @ts-ignore
    const { adminAwardingRule, adminAppData } = useSelector((state) => state);
    const { dataviewId, parameters } = adminAwardingRule;
    const { dataviews, customDateFormat } = adminAppData;

    /**
    * @typedef {{value: string, label: string, disabled: boolean}} SelectDataviewFieldOption
    * @type {SelectDataviewFieldOption[]}
    */
    const dataviewFields = dataviews.find((x) => x.id === dataviewId)?.fields.map((x) => ({
        value: x.key,
        label: x.label,
        disabled: parameters.findIndex((parameter) => parameter.dataviewKeyAlias === x.key) !== -1,
    })) || [];

    /**
     * @typedef {{value: SortOrderType, label: string}} SelectOption
     * @type {SelectOption[]}
     */
    const sortOrderTypeOptions = Object.values(SortOrderType).map((x) => ({
        value: x, label: getSortOrderTypeLabel(x),
    }));

    /**
     * @typedef {{value: DataviewFieldDataType | number, label: string}} DataTypeSelectOption
     * @type {DataTypeSelectOption[]}
     */
    const dataviewFieldDataTypeOptions = [{ value: DataviewFieldDataType.Numeric, label: 'Number' }];
    for (const format of customDateFormat) {
        dataviewFieldDataTypeOptions.push({ value: format.id, label: `Date (${format.name})` });
    }

    const handleOptionDeleted = () => {
        parameters.splice(index, 1);

        dispatch({
            type: UPDATE_AWARDING_RULE,
            payload: {
                parameters,
            },
        });
    };

    /**
     * @param {number} indexToMove
     * @param {number} direction
     */
    const handleOptionMove = (indexToMove, direction) => {
        [parameters[indexToMove]] = parameters.splice(indexToMove + direction, 1, parameters[indexToMove]);
        dispatch({
            type: UPDATE_AWARDING_RULE,
            payload: {
                parameters,
            },
        });
    };

    return (
        <Grid
            container
            justifyContent="space-evenly"
        >
            <Grid
                item
                className="awarding-param-grid-item"
            >
                <InputTitle className="awarding-param-index">{`${index + 1}.`}</InputTitle>
                {
                    parameters.length > 1 && (
                        <div
                            className="awarding-param-index-buttons-container"
                        >
                            <IconButton
                                onClick={() => handleOptionMove(index, Direction.Up)}
                                className="awarding-param-index-button"
                                disabled={isDisabled || index === 0}
                            >
                                <ArrowUpward />
                            </IconButton>
                            <IconButton
                                onClick={() => handleOptionMove(index, Direction.Down)}
                                className="awarding-param-index-button"
                                disabled={isDisabled || parameters.length === index + 1}
                            >
                                <ArrowDownward />
                            </IconButton>
                        </div>
                    )
                }
            </Grid>
            <Grid
                item
                xs={5}
                className="awarding-param-grid-item"
            >
                <InputTitle>Dataview Field</InputTitle>
                <StyledSelect
                    labelId="select-custom-field-label"
                    id="select-custom-field-type"
                    disabled={isDisabled}
                    renderValue={(/** @type {string} */
                        _value,
                    ) => dataviewFields.find((x) => x.value === dataviewParam.dataviewKeyAlias)?.label}
                    value={dataviewFields.find((x) => x.value === dataviewParam.dataviewKeyAlias)}
                    onChange={(/** @type {React.ChangeEvent<{ name?: string; value: string }>} */
                        event,
                    ) => handleOptionSelected(index, 'dataviewKeyAlias', event.target.value)}
                >
                    {dataviewFields.map((x) => (
                        <MenuItem
                            value={x.value}
                            key={`SelectItem_${x.label}`}
                            disabled={x.disabled}
                        >
                            {x.label}
                        </MenuItem>
                    ))}
                </StyledSelect>
                {
                    isFieldInvalid ? <span style={{ color: 'red' }}>Please select a dataview field</span> : null
                }
            </Grid>
            <Grid
                item
                xs={2}
                className="awarding-param-grid-item"
            >
                <InputTitle>Order type</InputTitle>
                <StyledSelect
                    labelId="select-sort-order-type-label"
                    id="select-sort-order-type-type"
                    disabled={isDisabled}
                    renderValue={(/** @type {string} */
                        _value,
                    ) => sortOrderTypeOptions.find((x) => x.value === dataviewParam.sortOrderType)?.label}
                    value={sortOrderTypeOptions.find((x) => x.value === dataviewParam.sortOrderType)}
                    onChange={(/** @type {React.ChangeEvent<{ name?: string; value: string }>} */
                        event,
                    ) => handleOptionSelected(index, 'sortOrderType', event.target.value)}
                >
                    {sortOrderTypeOptions.map((x) => (
                        <MenuItem
                            value={x.value}
                            key={`SelectItem_${x.label}`}
                        >
                            {x.label}
                        </MenuItem>
                    ))}
                </StyledSelect>
            </Grid>
            <Grid
                item
                xs={2}
                className="awarding-param-grid-item"
            >
                <InputTitle>Data type</InputTitle>
                <StyledSelect
                    labelId="select-data-type-label"
                    id="select-data-type-type"
                    disabled={isDisabled}
                    renderValue={(/** @type {string} */
                        _value,
                    ) => dataviewFieldDataTypeOptions.find((x) => (x.value === dataviewParam.dataType && dataviewParam.dataType === 0)
                        || x.value === dataviewParam.customDateFormatId)?.label}
                    value={dataviewFieldDataTypeOptions.find((x) => (x.value === dataviewParam.dataType && dataviewParam.dataType === 0)
                        || x.value === dataviewParam.customDateFormatId)}
                    onChange={(/** @type {React.ChangeEvent<{ name?: string; value: string }>} */
                        event,
                    ) => handleOptionSelected(index, 'dataType', event.target.value)}
                >
                    {dataviewFieldDataTypeOptions.map((x) => (
                        <MenuItem
                            value={x.value}
                            key={`SelectItem_${x.label}`}
                        >
                            {x.label}
                        </MenuItem>
                    ))}
                </StyledSelect>
            </Grid>
            <Grid
                item
                xs={1}
                className="awarding-param-grid-item"
                style={{ display: 'flex', alignItems: 'center' }}
            >
                {parameters.length > 1 && (
                    <IconButton
                        onClick={handleOptionDeleted}
                        disabled={isDisabled}
                    >
                        <DeleteIcon />
                    </IconButton>
                )}
            </Grid>
        </Grid>
    );
}

export default ParametersSection;
